import { useState, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import HistoricoFiltros from './components/HistoricoFiltros';
import HistoricoTable from './components/HistoricoTable';
import ClassificacaoBadge from './components/ClassificacaoBadge';
import HistoricoAddModal, { type NovoRegistoHistorico } from './components/HistoricoAddModal';

export type ClassificacaoServico = 'Excelente' | 'Relevante' | 'Adequado' | 'Inadequado' | 'Muito Bom' | 'Bom' | 'Regular';

export interface RegistoHistorico {
  id: string;
  funcionarioId: string;
  nomeFuncionario: string;
  carreira: string;
  ano: number;
  pontuacaoChefe: number;
  pontuacaoCCA: number;
  classificacaoServico: ClassificacaoServico;
  fonte: 'siadap' | 'historico';
}

const classificacaoOrder: ClassificacaoServico[] = ['Excelente', 'Relevante', 'Adequado', 'Inadequado', 'Muito Bom', 'Bom', 'Regular'];

export default function HistoricoPage() {
  const { funcionarios } = useFuncionarios();
  const { registosSiadap, registosHistorico, loadingSiadap, reloadSiadap } = useSiadap();

  const [search, setSearch] = useState('');
  const [anoFilter, setAnoFilter] = useState('');
  const [classificacaoFilter, setClassificacaoFilter] = useState('');
  const [carreiraFilter, setCarreiraFilter] = useState('');
  const [groupByFuncionario, setGroupByFuncionario] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editRegisto, setEditRegisto] = useState<RegistoHistorico | null>(null);
  const [deleteRegisto, setDeleteRegisto] = useState<RegistoHistorico | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const registos = useMemo<RegistoHistorico[]>(() => {
    const siadapItems: RegistoHistorico[] = registosSiadap
      .map((r) => {
        const func = funcionarios.find((f) => f.id === r.funcionarioId);
        if (!func) return null;
        const partes = r.periodoAvaliacao.split('-');
        const anoFinal = partes.length >= 2 ? parseInt(partes[partes.length - 1], 10) : parseInt(partes[0], 10);
        const ano = isNaN(anoFinal) ? new Date().getFullYear() : anoFinal;
        return {
          id: `siadap-${r.id}`,
          funcionarioId: func.id,
          nomeFuncionario: func.nome,
          carreira: func.carreira,
          ano,
          pontuacaoChefe: r.pontosChefe,
          pontuacaoCCA: r.pontosCCA,
          classificacaoServico: r.avaliacao as ClassificacaoServico,
          fonte: 'siadap' as const,
        };
      })
      .filter((r): r is RegistoHistorico => r !== null);

    const historicoItems: RegistoHistorico[] = registosHistorico.map((r) => ({
      id: r.id,
      funcionarioId: r.funcionarioId,
      nomeFuncionario: r.nomeFuncionario,
      carreira: r.carreira,
      ano: r.ano,
      pontuacaoChefe: r.pontuacaoChefe,
      pontuacaoCCA: r.pontuacaoCCA,
      classificacaoServico: r.classificacaoServico as ClassificacaoServico,
      fonte: 'historico' as const,
    }));

    return [...siadapItems, ...historicoItems].sort((a, b) => b.ano - a.ano);
  }, [registosSiadap, registosHistorico, funcionarios]);

  const loading = loadingSiadap;

  function showSuccess(msg: string) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  async function handleAddRegisto(data: NovoRegistoHistorico) {
    const id = `hp-${Date.now()}`;
    const { error } = await supabase.from('historico_progressoes').insert({
      id,
      funcionario_id: data.funcionarioId,
      nome_funcionario: data.nomeFuncionario,
      carreira: data.carreira,
      ano: data.ano,
      pontuacao_chefe: data.pontuacaoChefe,
      pontuacao_cca: data.pontuacaoCCA,
      classificacao_servico: data.classificacaoServico,
    });
    if (!error) {
      showSuccess('Registo adicionado com sucesso!');
      setShowAddModal(false);
      await reloadSiadap();
    } else {
      showSuccess('Erro ao guardar registo.');
    }
  }

  async function handleEditRegisto(data: NovoRegistoHistorico) {
    if (!editRegisto) return;
    const { error } = await supabase.from('historico_progressoes').update({
      funcionario_id: data.funcionarioId,
      nome_funcionario: data.nomeFuncionario,
      carreira: data.carreira,
      ano: data.ano,
      pontuacao_chefe: data.pontuacaoChefe,
      pontuacao_cca: data.pontuacaoCCA,
      classificacao_servico: data.classificacaoServico,
    }).eq('id', editRegisto.id);
    if (!error) {
      showSuccess('Registo atualizado com sucesso!');
      setEditRegisto(null);
      await reloadSiadap();
    } else {
      showSuccess('Erro ao atualizar registo.');
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteRegisto) return;
    setDeleting(true);
    const { error } = await supabase.from('historico_progressoes').delete().eq('id', deleteRegisto.id);
    setDeleting(false);
    if (!error) {
      showSuccess('Registo eliminado.');
      setDeleteRegisto(null);
      await reloadSiadap();
    } else {
      showSuccess('Erro ao eliminar registo.');
    }
  }

  const anos = useMemo(() => Array.from(new Set(registos.map((r) => r.ano))).sort((a, b) => b - a), [registos]);
  const carreiras = useMemo(() => Array.from(new Set(registos.map((r) => r.carreira))).sort(), [registos]);

  const filtered = useMemo(() => registos.filter((r) => {
    const matchSearch = search === '' || r.nomeFuncionario.toLowerCase().includes(search.toLowerCase()) || r.funcionarioId.includes(search);
    const matchAno = anoFilter === '' || r.ano === parseInt(anoFilter, 10);
    const matchClassificacao = classificacaoFilter === '' || r.classificacaoServico === classificacaoFilter;
    const matchCarreira = carreiraFilter === '' || r.carreira === carreiraFilter;
    return matchSearch && matchAno && matchClassificacao && matchCarreira;
  }), [registos, search, anoFilter, classificacaoFilter, carreiraFilter]);

  const hasFilters = search || anoFilter || classificacaoFilter || carreiraFilter;
  function clearFilters() { setSearch(''); setAnoFilter(''); setClassificacaoFilter(''); setCarreiraFilter(''); }

  const totalRegistos = filtered.length;
  const mediaChefe = filtered.length > 0 ? (filtered.reduce((s, r) => s + r.pontuacaoChefe, 0) / filtered.length).toFixed(2) : '—';
  const mediaCCA = filtered.length > 0 ? (filtered.reduce((s, r) => s + r.pontuacaoCCA, 0) / filtered.length).toFixed(2) : '—';
  const countByClass = classificacaoOrder.map((c) => ({ label: c, count: filtered.filter((r) => r.classificacaoServico === c).length }));
  const siadapCount = registos.filter((r) => r.fonte === 'siadap').length;
  const historicoCount = registos.filter((r) => r.fonte === 'historico').length;

  return (
    <>
      <main className="flex-1">

        {/* PAGE HEADER */}
        <div className="bg-white border-b border-zinc-100 px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-history-line text-white text-lg" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Registos Avaliativos</span>
                </div>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Histórico de Progressões</h1>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-100 px-2.5 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    {siadapCount} do SIADAP
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-100 px-2.5 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                    {historicoCount} manuais
                  </span>
                  {anos.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-100 px-2.5 py-1 rounded-full">
                      <i className="ri-calendar-line text-xs" />
                      {anos[anos.length - 1]}–{anos[0]}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="whitespace-nowrap flex items-center gap-1.5 text-[13px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-close-circle-line text-sm" /></div>
                  Limpar filtros
                </button>
              )}
              <button
                onClick={() => setShowAddModal(true)}
                className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                Novo Registo
              </button>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-6">

          {successMsg && (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] px-4 py-3 rounded-xl">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-sm" /></div>
              {successMsg}
            </div>
          )}

          {loading ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3 text-zinc-300">
              <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin" /></div>
              <p className="text-[13px] text-zinc-400">A carregar histórico...</p>
            </div>
          ) : (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-history-line', value: totalRegistos, label: 'Registos filtrados', sub: `${registos.length} no total`, iconBg: 'bg-zinc-100 text-zinc-600' },
                  { icon: 'ri-user-star-line', value: mediaChefe, label: 'Média Pont. Chefe', sub: 'Pontuação média', iconBg: 'bg-zinc-100 text-zinc-600' },
                  { icon: 'ri-shield-check-line', value: mediaCCA, label: 'Média Pont. CCA', sub: 'Pontuação média', iconBg: 'bg-emerald-50 text-emerald-600' },
                  { icon: 'ri-bar-chart-grouped-line', value: anos.length, label: 'Anos com registos', sub: anos.length > 0 ? `${anos[anos.length - 1]} a ${anos[0]}` : '—', iconBg: 'bg-amber-50 text-amber-600' },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${s.iconBg}`}>
                      <i className={`${s.icon} text-lg`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[22px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                      <p className="text-[11px] font-medium text-zinc-600 mt-0.5">{s.label}</p>
                      <p className="text-[11px] text-zinc-400 truncate">{s.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Distribuição + Filtros */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Distribuição por classificação */}
                <div className="bg-white rounded-xl border border-zinc-100 p-5">
                  <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-4">Distribuição</p>
                  <div className="space-y-2.5">
                    {countByClass.filter((c) => c.count > 0).map(({ label, count }) => {
                      const pct = totalRegistos > 0 ? (count / totalRegistos) * 100 : 0;
                      return (
                        <div key={label}>
                          <div className="flex items-center justify-between mb-1">
                            <ClassificacaoBadge classificacao={label as ClassificacaoServico} />
                            <span className="text-[12px] font-bold text-zinc-700">{count}</span>
                          </div>
                          <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                            <div className="h-full bg-zinc-400 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    {countByClass.every((c) => c.count === 0) && (
                      <p className="text-[12px] text-zinc-400 text-center py-2">Sem dados</p>
                    )}
                  </div>
                </div>

                {/* Filtros + View toggle */}
                <div className="lg:col-span-3 space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-1 bg-white border border-zinc-200 rounded-lg p-1">
                      <button
                        onClick={() => setGroupByFuncionario(true)}
                        className={`whitespace-nowrap flex items-center gap-1.5 text-[13px] px-3 py-1.5 rounded-md transition-colors cursor-pointer ${groupByFuncionario ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-800'}`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-group-line text-sm" /></div>
                        Por Funcionário
                      </button>
                      <button
                        onClick={() => setGroupByFuncionario(false)}
                        className={`whitespace-nowrap flex items-center gap-1.5 text-[13px] px-3 py-1.5 rounded-md transition-colors cursor-pointer ${!groupByFuncionario ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-800'}`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-list-unordered text-sm" /></div>
                        Lista Completa
                      </button>
                    </div>
                    <p className="text-[12px] text-zinc-400">
                      <span className="font-semibold text-zinc-600">{filtered.length}</span> registo(s)
                    </p>
                  </div>

                  <HistoricoFiltros
                    search={search} onSearchChange={setSearch}
                    anoFilter={anoFilter} onAnoChange={setAnoFilter}
                    classificacaoFilter={classificacaoFilter} onClassificacaoChange={setClassificacaoFilter}
                    carreiraFilter={carreiraFilter} onCarreiraChange={setCarreiraFilter}
                    carreiras={carreiras} anos={anos}
                  />
                </div>
              </div>

              <HistoricoTable
                registos={filtered}
                groupByFuncionario={groupByFuncionario}
                onEdit={(r) => setEditRegisto(r)}
                onDelete={(r) => setDeleteRegisto(r)}
              />
            </>
          )}

          {!loading && (
            <div className="flex items-center justify-between text-[11px] text-zinc-400 pb-2">
              <span>SIADAP para RH · Câmara Municipal</span>
              <span>Atualizado em {new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            </div>
          )}
        </div>
      </main>

      {showAddModal && (
        <HistoricoAddModal onSave={handleAddRegisto} onClose={() => setShowAddModal(false)} />
      )}

      {editRegisto && (
        <HistoricoAddModal
          initialData={{
            funcionarioId: editRegisto.funcionarioId,
            nomeFuncionario: editRegisto.nomeFuncionario,
            carreira: editRegisto.carreira,
            ano: editRegisto.ano,
            pontuacaoChefe: editRegisto.pontuacaoChefe,
            pontuacaoCCA: editRegisto.pontuacaoCCA,
            classificacaoServico: editRegisto.classificacaoServico,
          }}
          isEdit
          onSave={handleEditRegisto}
          onClose={() => setEditRegisto(null)}
        />
      )}

      {deleteRegisto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setDeleteRegisto(null)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-2xl w-full max-w-sm overflow-hidden border border-zinc-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-5 flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-rose-50 text-rose-500">
                <i className="ri-delete-bin-line text-xl" />
              </div>
              <div>
                <h3 className="font-bold text-zinc-900 text-[15px]">Eliminar Registo</h3>
                <p className="text-[13px] text-zinc-500 mt-1.5 leading-relaxed">
                  Tem a certeza que quer eliminar o registo de <strong className="text-zinc-700">{deleteRegisto.nomeFuncionario}</strong> do ano <strong className="text-zinc-700">{deleteRegisto.ano}</strong>? Esta ação não pode ser desfeita.
                </p>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setDeleteRegisto(null)}
                className="whitespace-nowrap flex-1 px-4 py-2.5 text-[13px] font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="whitespace-nowrap flex-1 px-4 py-2.5 text-[13px] font-medium bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {deleting ? <><i className="ri-loader-4-line animate-spin text-sm" />A eliminar...</> : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
